import { useState, type SyntheticEvent } from "react";

import {
  Button,
  controlClassName,
  Input,
  Segmented,
  Sheet,
} from "../../../shared/ui";
import {
  AUDIT_OUTCOMES,
  AUDIT_PERIODS,
  DEFAULT_AUDIT_PERIOD,
  isAuditPeriod,
  type AuditFilter,
  type AuditOutcome,
} from "../adminRoute";
import { isUuid, outcomeLabel } from "./auditFormat";
import { eventTypeLabel, useAuditT } from "./useAuditT";

/** Filter controls. Every change becomes a new filter handed to the URL. */
function FilterFields({
  filter,
  eventTypes,
  onChange,
}: {
  filter: AuditFilter;
  eventTypes: string[];
  onChange: (next: AuditFilter) => void;
}) {
  const t = useAuditT();
  const [operator, setOperator] = useState(filter.operator ?? "");
  const [user, setUser] = useState(filter.user ?? "");
  const [invalid, setInvalid] = useState(false);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const operatorId = operator.trim();
    const userId = user.trim();
    if (
      (operatorId !== "" && !isUuid(operatorId)) ||
      (userId !== "" && !isUuid(userId))
    ) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange({
      ...filter,
      operator: operatorId === "" ? undefined : operatorId,
      user: userId === "" ? undefined : userId,
    });
  };

  const types =
    filter.type !== undefined && !eventTypes.includes(filter.type)
      ? [filter.type, ...eventTypes]
      : eventTypes;

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <Segmented
        label={t("admin.audit.filter.period")}
        value={filter.period ?? DEFAULT_AUDIT_PERIOD}
        options={AUDIT_PERIODS.map((id) => ({
          id,
          label: t(`admin.audit.period.${id}`),
        }))}
        onChange={(id) => {
          if (isAuditPeriod(id)) onChange({ ...filter, period: id });
        }}
      />
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.audit.filter.type")}
        <select
          className={controlClassName}
          value={filter.type ?? ""}
          onChange={(event) => {
            onChange({ ...filter, type: event.target.value || undefined });
          }}
        >
          <option value="">{t("admin.audit.filter.any")}</option>
          {types.map((type) => (
            <option key={type} value={type}>
              {eventTypeLabel(t, type)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.audit.filter.outcome")}
        <select
          className={controlClassName}
          value={filter.outcome ?? ""}
          onChange={(event) => {
            const value = event.target.value;
            const outcome: AuditOutcome | undefined = AUDIT_OUTCOMES.find(
              (candidate) => candidate === value,
            );
            onChange({ ...filter, outcome });
          }}
        >
          <option value="">{t("admin.audit.filter.any")}</option>
          {AUDIT_OUTCOMES.map((outcome) => (
            <option key={outcome} value={outcome}>
              {outcomeLabel(t, outcome)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.audit.filter.operator")}
        <Input
          value={operator}
          maxLength={36}
          className="font-mono"
          aria-invalid={invalid && operator.trim() !== "" && !isUuid(operator)}
          onChange={(event) => {
            setOperator(event.target.value);
          }}
        />
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.audit.filter.user")}
        <Input
          value={user}
          maxLength={36}
          className="font-mono"
          aria-invalid={invalid && user.trim() !== "" && !isUuid(user)}
          onChange={(event) => {
            setUser(event.target.value);
          }}
        />
      </label>
      {invalid && (
        <p role="alert" className="text-footnote text-error">
          {t("admin.audit.filter.invalidId")}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" variant="secondary">
          {t("admin.audit.filters.apply")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setOperator("");
            setUser("");
            setInvalid(false);
            onChange({});
          }}
        >
          {t("admin.audit.filters.reset")}
        </Button>
      </div>
    </form>
  );
}

/**
 * Wide: fields inline. Narrow: a "Filters" button opens them in a sheet.
 * The fields are re-keyed by the filter so URL changes reset the drafts.
 */
export function AuditFilters({
  filter,
  eventTypes,
  wide,
  onChange,
}: {
  filter: AuditFilter;
  eventTypes: string[];
  wide: boolean;
  onChange: (next: AuditFilter) => void;
}) {
  const t = useAuditT();
  const [open, setOpen] = useState(false);
  const key = JSON.stringify(filter);
  if (wide) {
    return (
      <FilterFields
        key={key}
        filter={filter}
        eventTypes={eventTypes}
        onChange={onChange}
      />
    );
  }
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("admin.audit.filters")}
      </Button>
      <Sheet
        open={open}
        onOpenChange={setOpen}
        title={t("admin.audit.filters")}
      >
        <FilterFields
          key={key}
          filter={filter}
          eventTypes={eventTypes}
          onChange={(next) => {
            setOpen(false);
            onChange(next);
          }}
        />
      </Sheet>
    </>
  );
}
